import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { compose } from 'recompose'
import { join as joinPromise } from 'bluebird'
import ImmutablePropTypes from 'react-immutable-proptypes'
import partial from 'lodash/partial'
import replace from 'lodash/replace'
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import isFunction from 'lodash/isFunction'
import once from 'lodash/once'
import isNil from 'lodash/isNil'
import { Map, List } from 'immutable'
import { Provider, connect as connectRedux } from 'react-redux'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import { ApolloProvider } from '@apollo/client'
import throttle from 'lodash/throttle'
import CommentsLoader from '../CommentsLoader/index'
import VideoPlayerApp from '../VideoPlayerApp/index'
import VideoPlayerSpinner from '../VideoPlayerSpinner/index'
import VideoPlayerEnded from '../VideoPlayerEnded/index'
import VideoEmailCapture from '../VideoEmailCapture/index'
import { OracleGuideOnSidePluginContent } from '../OracleSideGuide/OracleGuideOnSidePluginContent'
import HistoryContext from '../Router/HistoryContext'
import { getBoundActions } from '../../actions/index'
import { get as getConfig } from '../../config/index'
import {
  getVideoOpts,
  getContainerStyles,
  setupVideoPlayer,
} from '../../video-js/index'
import {
  TYPE_PREVIEW,
  TYPE_FEATURE,
  VIDEO_PLAYER_INACTIVITY_TIMEOUT,
  MOBILE_VIDEO_PLAYER_INACTIVITY_TIMEOUT,
} from '../../services/video/index'
import { TYPE_CONTENT_VIDEO, TYPE_CONTENT_SERIES } from '../../services/content-type/index'
import { STORE_KEY_VIDEO_PLAYER_NEXT } from '../../services/store-keys/index'
import { getAuthIsLoggedIn, getAuthJwt } from '../../services/auth/index'
import ContentUpdater from '../ContentUpdater/index'
import { getLocalPreferences, LOCAL_PREFERENCE_VIDEO_PLAYBACK_RATE, setLocalPreferences } from '../../services/local-preferences/index'
import { posthog } from '../PostHog/index'
import { isIOS } from '../../environment/index'
import {
  VIDEO_PLAYER_OPENED_GA4_EVENT,
  VIDEO_LOADS_AND_STARTS_PLAY_GA4_EVENT,
  VIDEO_QUALIFIED_VIEW_GA4_EVENT,
  VIDEO_ENGAGED_VIEW_GA4_EVENT,
  VIDEO_COMPLETED_GA4_EVENT,
  VIDEO_SPEED_CHANGED_GA4_EVENT,
  VIDEO_AUDIO_CHANGED_GA4_EVENT,
  VIDEO_SUBTITLES_SET_GA4_EVENT,
  PLAYER_INFO_TOGGLED_GA4_EVENT,
  CHROMECAST_CONNECTED_GA4_EVENT,
  AIRPLAY_CONNECTED_GA4_EVENT,
  VIDEO_INTERESTED_VIEW_GA4_EVENT,
  VIDEO_FIVE_MIN_VIEW_GA4_EVENT,
  AI_RECOMMENDATION_PLAYED_GA4_EVENT,
  APP_ERROR_EXPERIENCED_GA4_EVENT,
} from '../../services/event-tracking/index'
import { getPrimary } from '../../services/languages/index'

import {
  getSavedAudioLanguage,
  getActiveAudioLanguageTrack,
  saveAudioPreferences,
  saveSubtitlesPreferences,
  getSubtitlesPreferences,
} from './video-media-center-preferences'

const KEY_SPACEBAR = 32
const config = getConfig()
const tilesV2GraphqlActive = !!get(config, ['features', 'tilesV2Graphql'])

const SEEK_THROTTLE_INTERVAL_MS = 350 // Minimum interval between seeks when holding arrow keys

function renderVideoPlayerApps (props, player) {
  const {
    store, history, location, comments, apolloClient, primaryCourse,
  } = props
  return (
    <ApolloProvider client={apolloClient}>
      <Provider store={store}>
        <HistoryContext.Provider value={history}>
          <VideoPlayerApp
            player={player}
            history={history}
            location={location}
            commentsVisible={comments.get('visible', false)}
            primaryCourse={primaryCourse}
          />
        </HistoryContext.Provider>
      </Provider>
    </ApolloProvider>
  )
}

/**
 * Render function for Oracle Guide on Side plugin.
 * Creates the React component to mount inside the video.js player.
 * Note: JWT is obtained from Redux directly by the component (reactive to auth changes).
 */
function renderOracleGuideOnSide (player, options) {
  const {
    store, apolloClient, history, contentId, featureMediaId,
  } = options
  return (
    <ApolloProvider client={apolloClient}>
      <Provider store={store}>
        <HistoryContext.Provider value={history}>
          <OracleGuideOnSidePluginContent
            player={player}
            contentId={contentId}
            featureMediaId={featureMediaId}
          />
        </HistoryContext.Provider>
      </Provider>
    </ApolloProvider>
  )
}

/**
 * Initialize video JS plugins
 * Chromecast and Airplay plugins are configured via the Brightcove CMS
 * @see https://studio.brightcove.com/products/videocloud/players
 */
async function initVideoJsPlugins () {
  const [
    VideoPlayerAppsPlugin,
    SMFAnalyticsPlugin,
    SeekButtonsPlugin,
    KeyboardPlaybackTogglePlugin,
    VideoOverlayPlugin,
    VideoMediaCenterPlugin,
    VideoChaptersPlugin,
    // VideoMediaCenterBakPlugin,
    VolumeBarPlugin,
    PlayFromBeginningButtonPlugin,
    OracleGuideOnSidePlugin,
  ] = await joinPromise(
    import('../../video-js/video-player-app/index'),
    import('../../video-js/smf-analytics/index'),
    import('../../video-js/seek-buttons/index'),
    import('../../video-js/keyboard-playback-toggle/index'),
    import('../../video-js/video-overlay-button/index'),
    import('../../video-js/video-media-center/index'),
    import('../../video-js/video-chapters/index'),
    import('../../video-js/volume-bar/index'),
    import('../../video-js/play-from-beginning-button/index'),
    import('../../video-js/oracle-guide-on-side/index'),
  )
  const registeredPlugins = global.videojs.getPlugins()
  if (registeredPlugins[VideoPlayerAppsPlugin.PLUGIN_NAME] === undefined) {
    VideoPlayerAppsPlugin.register(global.videojs)
  }
  if (registeredPlugins[SMFAnalyticsPlugin.PLUGIN_NAME] === undefined) {
    SMFAnalyticsPlugin.register(global.videojs)
  }
  if (registeredPlugins[SeekButtonsPlugin.PLUGIN_NAME] === undefined) {
    SeekButtonsPlugin.register(global.videojs)
  }
  if (registeredPlugins[KeyboardPlaybackTogglePlugin.PLUGIN_NAME] === undefined) {
    KeyboardPlaybackTogglePlugin.register(global.videojs)
  }
  if (registeredPlugins[VideoOverlayPlugin.PLUGIN_NAME] === undefined) {
    VideoOverlayPlugin.register(global.videojs)
  }
  if (registeredPlugins[VideoMediaCenterPlugin.PLUGIN_NAME] === undefined) {
    VideoMediaCenterPlugin.register(global.videojs)
  }
  if (registeredPlugins[VideoChaptersPlugin.PLUGIN_NAME] === undefined) {
    VideoChaptersPlugin.register(global.videojs)
  }
  if (registeredPlugins[VolumeBarPlugin.PLUGIN_NAME] === undefined) {
    VolumeBarPlugin.register(global.videojs)
  }
  if (registeredPlugins[PlayFromBeginningButtonPlugin.PLUGIN_NAME] === undefined) {
    PlayFromBeginningButtonPlugin.register(global.videojs)
  }
  if (registeredPlugins[OracleGuideOnSidePlugin.PLUGIN_NAME] === undefined) {
    OracleGuideOnSidePlugin.register(global.videojs)
  }
}

function renderComments (props, player, playOrPause) {
  let onClickCloseComments
  try {
    if (player && !player.ended()) {
      onClickCloseComments = partial(playOrPause, true)
    }
  } catch (e) {
    // Do nothing player callback for ended failed
  }
  const showComments = get(config, ['features', 'comments'], false)
  if (!showComments || dataHasError(props)) {
    return null
  }
  return <CommentsLoader onClickClose={onClickCloseComments} location={props.location} />
}

function renderVideoEmailCapture (props, containerStyles) {
  const { auth, videoPlayer, emailSignup } = props
  const { emailCaptureBannerHeight } = containerStyles
  const isLoading = videoPlayer.get('loading', false)
  const authenticated = getAuthIsLoggedIn(auth)
  const gatedPreviews = get(config, ['features', 'player', 'gatedPreviews'])
  const emailSignupCompleted = emailSignup.get('completed', false)

  // don't render the email capture if the user has already signed up
  if (gatedPreviews && !authenticated && emailSignupCompleted) {
    return null
  }
  if (!isLoading && !authenticated) {
    return (
      <VideoEmailCapture emailCaptureBannerHeight={emailCaptureBannerHeight} />
    )
  }
  return null
}

function dataHasError (props) {
  const { video } = props
  return (
    video.getIn(['data', '_dataError'])
  )
}

function renderVideoPlayerEnded (props) {
  const {
    videoPlayer,
    type,
    video,
    auth,
    location,
    history,
    tiles,
    getTilesData,
    deleteTiles,
    displayVideoEnded,
    setVideoPlayerEnded,
    user,
    primaryCourse,
  } = props
  const videoEnded = videoPlayer.get('ended', false)
  if (displayVideoEnded && videoEnded) {
    return (
      <VideoPlayerEnded
        user={user}
        type={type}
        video={video}
        auth={auth}
        location={location}
        history={history}
        tiles={tiles}
        getTilesData={getTilesData}
        deleteTiles={deleteTiles}
        setVideoPlayerEnded={setVideoPlayerEnded}
        primaryCourse={primaryCourse}
      />
    )
  }
  return null
}

class VideoPlayer extends Component {
  state = {
    containerStyles: {},
    videoReady: false,
    sendGa4Events: false,
    isFocused: false,
    isHovered: false,
  }

  constructor (props) {
    super(props)
    const { playerRenderDelayMilliseconds } = props
    this.renderPlayerTimeout = setTimeout(() => {
      this.renderPlayerTimeout = undefined
      // Player already rendered
      if (this.state.renderPlayer) {
        return
      }
      this.setState({ renderPlayer: true })
    }, playerRenderDelayMilliseconds > 0 ? playerRenderDelayMilliseconds : 0)
  }

  onPlayerFocus = () => {
    this.setState({ isFocused: true })
  }

  onPlayerBlur = () => {
    this.setState({ isFocused: false })
  }

  onPlayerMouseOver = (e) => {
    // Only set hovered if coming from outside the player
    if (!e.currentTarget.contains(e.relatedTarget)) {
      this.setState({ isHovered: true })
    }
  }

  onPlayerMouseOut = (e) => {
    // Only unset hovered if leaving the player entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      this.setState({ isHovered: false })
    }
  }

  componentDidMount () {
    const { props } = this
    const { setVideoPlayerFullscreen, setMetaVotesVisible } = props

    setVideoPlayerFullscreen(false)
    setMetaVotesVisible(false)

    // needed only for the <VideoPage /> and <ShareVideoViewPage /> implementations
    // needed to set player to be full window size
    this.setContainerStyles()
    this._onWindowResize = debounce(this.setContainerStyles, 100)
    global.addEventListener('resize', this._onWindowResize)
    window.addEventListener('keydown', this.handleGlobalKeyDown)
  }

  componentDidUpdate (prevProps, prevState) {
    // Always run the JWT sync logic
    const { auth = Map() } = this.props
    const { prevAuth = Map() } = prevProps
    if (auth !== prevAuth && this._player && this._player.setState) {
      // Keep the video player state in sync
      try {
        this._player.setState({ jwt: getAuthJwt(auth) })
      } catch (_) {
        // If this fails catch the error to prevent the render from breaking
      }
    }
    // Arrow key seeking logic: attach/detach event listener based on focus/hover
    const shouldBeActive = this.state.isFocused || this.state.isHovered
    const wasActive = prevState ? (prevState.isFocused || prevState.isHovered) : false
    if (shouldBeActive && !wasActive) {
      window.addEventListener('keydown', this.handleGlobalKeyDown)
    } else if (!shouldBeActive && wasActive) {
      window.removeEventListener('keydown', this.handleGlobalKeyDown)
    }
  }

  componentWillUnmount () {
    const { props } = this
    const {
      setCommentsVisible,
      setVideoPlayerEnded,
      setUserVideoDataFeaturePosition,
      setMetaVotesVisible,
    } = props
    if (this.renderPlayerTimeout) {
      clearTimeout(this.renderPlayerTimeout)
      this.renderPlayerTimeout = undefined
    }
    if (this._onWindowResize) {
      global.removeEventListener('resize', this._onWindowResize)
    }
    try {
      if (this._player) {
        const currentTime = this._player.currentTime()
        setUserVideoDataFeaturePosition(currentTime)
      }
    } catch (e) {
      // Do nothing the player failed to get current time
    }
    setCommentsVisible(false)
    setVideoPlayerEnded(false)
    setMetaVotesVisible(false)
    this.disposePlayer()
    window.removeEventListener('keydown', this.handleGlobalKeyDown)
  }

  getGa4EventData () {
    const { props } = this
    const {
      media,
      video,
      upstreamContext = Map(),
      resolver = Map(),
    } = props
    const mediaId = media.getIn(['data', 'id'])
    const contentId = video.getIn(['data', 'id'])
    const playEventData = upstreamContext.get('playEventData')
    const clickAttribution = upstreamContext.getIn(['playEventData', 'clickAttribution']) || null
    const spotlightType = upstreamContext.getIn(['playEventData', 'spotlightType']) || null
    const spotlightCampaign = upstreamContext.getIn(['playEventData', 'spotlightCampaign']) || null
    const isFeaturedMedia = resolver.getIn(['query', 'fullplayer'], 'feature') === TYPE_FEATURE
    const recommId = upstreamContext.getIn(['playEventData', 'recommId']) || null

    let eventData = {
      media_id: mediaId,
      content_id: contentId,
      content_name: video.getIn(['data', 'title']),
      content_type: video.getIn(['data', 'contentType']),
      media_language: media.getIn(['data', 'mediaLang']),
      is_featured_media: isFeaturedMedia,
    }

    if (video.getIn(['data', 'seriesId'])) {
      eventData = {
        ...eventData,
        parent_series_name: video.getIn(['data', 'seriesTitle']),
        parent_series_id: video.getIn(['data', 'seriesId']),
      }
    }

    if (Map.isMap(playEventData) && playEventData.size > 0) {
      eventData = {
        ...eventData,
        row_placement: upstreamContext.getIn(['playEventData', 'rowPlacement']),
        tile_number: upstreamContext.getIn(['playEventData', 'tileNumber']),
        row_name: upstreamContext.getIn(['playEventData', 'rowName']),
        ...(clickAttribution && { click_attribution: clickAttribution }),
        ...(spotlightType && { spotlight_type: spotlightType }),
        ...(spotlightCampaign && { spotlight_campaign: spotlightCampaign }),
        ...(recommId && { recomm_id: recommId }),
      }
    }

    return eventData
  }

  getGa4TileData () {
    const { props } = this
    const { upstreamContext = Map() } = props
    const playEventData = upstreamContext.get('playEventData')
    const clickAttribution = upstreamContext.getIn(['playEventData', 'clickAttribution']) || null

    let rowData = {}

    if (Map.isMap(playEventData) && playEventData.size > 0) {
      rowData = {
        row_placement: upstreamContext.getIn(['playEventData', 'rowPlacement']),
        tile_number: upstreamContext.getIn(['playEventData', 'tileNumber']),
        row_name: upstreamContext.getIn(['playEventData', 'rowName']),
        ...(clickAttribution && { click_attribution: clickAttribution }),
      }
    }

    return rowData
  }

  getAiRecommendationData () {
    const { props } = this
    const { upstreamContext = Map() } = props
    const clickAttribution = upstreamContext.getIn(['playEventData', 'clickAttribution']) || null
    const isAiRecommendation = upstreamContext.getIn(['playEventData', 'isAiRecommendation']) || false

    return isAiRecommendation ? {
      ...(clickAttribution && { click_attribution: clickAttribution }),
    } : null
  }

  onPlayerFailure = () => {
    const { setLoading, setEventDefault } = this.props
    setLoading(false)
    setEventDefault(APP_ERROR_EXPERIENCED_GA4_EVENT.mergeDeep({
      ...this.getGa4EventData(),
      error_type: 'PlayerLoaderError',
    }))
  }

  onPlayerSuccess = async (data) => {
    const { props } = this
    const {
      type,
      user,
      media,
      userVideo,
      playerSrc,
      setLoading,
      setMetaMoreVisible,
      video,
      auth,
      location,
      setEventDefault,
      setAiGa4Event,
    } = props
    let src = playerSrc
    if (!src) {
      const savedPreference = getSavedAudioLanguage(video, auth)
      const listOfVideos = media.getIn(['data', 'byLang'], null)
      src = listOfVideos.get(savedPreference, null)
    }
    setLoading(true)
    // always set this to false when loading in the video player
    setMetaMoreVisible(false)
    await initVideoJsPlugins()
    const { ref: dataPlayer } = data
    const aiRecommendationData = this.getAiRecommendationData()
    const component = this
    dataPlayer.ready(function onPlayerReady () {
      const player = setupVideoPlayer({
        player: this,
        media,
        type,
        user,
        userVideo,
        location,
        src: playerSrc,
        text: null,
        startAt: props.startAt || null,
        isLiveVideo: false,
      })
      if (!player) {
        return
      }
      component.setVideoPlayer(player)
      component.configurePlayerPlugins(player)
      component.registerPlayerEventHandlers(player)
      component.initPlaybackRate(player)
      component.setState({ videoReady: true })
    })
    setEventDefault(VIDEO_PLAYER_OPENED_GA4_EVENT.mergeDeep({
      ...this.getGa4EventData(),
      ...this.getGa4TileData(),
    }))
    if (aiRecommendationData) {
      setAiGa4Event(AI_RECOMMENDATION_PLAYED_GA4_EVENT.mergeDeep({
        ...this.getGa4EventData(),
        ...aiRecommendationData,
        // ui_element: 'AI Hyperlink',
        // ui_location: 'Video Player',
      }))
      // Direct posthog-js capture — this is a class component so we cannot use
      // the usePostHogEvent hook; the posthog-js singleton is initialized by
      // GaiaPostHogProvider at app root
      posthog.capture('ai_recommendation_played', {
        content_id: aiRecommendationData.content_id,
        content_type: aiRecommendationData.content_type,
      })
    }
  }

  getDuration = () => {
    const { props } = this
    const { media } = props
    return Map.isMap(media) ? media.getIn(['data', 'duration']) : 0
  }

  initPlaybackRate = (player) => {
    try {
      const { auth } = this.props
      const playbackRate = getLocalPreferences(auth.get('uid'), LOCAL_PREFERENCE_VIDEO_PLAYBACK_RATE)
      if (playbackRate) {
        player.defaultPlaybackRate(parseFloat(playbackRate))
      }
    } catch (e) {
      // Do nothing if this fail to be called possibly due to local storage permissions
    }
  }

  setContainerStyles = () => {
    const { props } = this
    if (props.skipContainerStyles) return
    this.setState(() => ({ containerStyles: getContainerStyles(props) }))
  }

  setVideoPlayer = (player) => {
    this._player = player
    if (this.props.onPlayerReady) {
      this.props.onPlayerReady(player)
    }
  }

  disposePlayer = () => {
    if (this._newRelicTracker && isFunction(this._newRelicTracker.dispose)) {
      this._newRelicTracker.dispose()
      this._newRelicTracker = undefined
    }
    if (!this._player) {
      return
    }
    // React player loader component will dispose player
    this._player = undefined
  }

  /**
   * Register Video Player event handlers
   * @param {*} dataPlayer The video player which was initialized
   */
  registerPlayerEventHandlers = (player) => {
    const { props } = this
    const {
      media,
      setLoading,
      onVideoPlay,
      onVideoEnded,
      setEventDefault,
      setVideoPlayerEnded,
      setVideoPlayerFullscreen,
      setMetaAutoHideVisibility,
      auth,
      setMetaVisible,
    } = props

    // Using self/that patter so we don't lose this in the listeners scopes
    const self = this

    const onceReloadLocation = once(() => {
      global.location.reload()
    })
    // Get a current instance of this player
    let playerEventTimeoutId

    function clearTimeoutPlayerEvent () {
      if (playerEventTimeoutId) {
        player.clearTimeout(playerEventTimeoutId)
      }
      playerEventTimeoutId = undefined
    }

    function setTimeoutPlayerEvent (func, timeout) {
      clearTimeoutPlayerEvent()
      playerEventTimeoutId = player.setTimeout(func, timeout)
    }

    function videoOverlayButtonClicked () {
      const { videoPlayerInfoDisabled } = this.state

      if (videoPlayerInfoDisabled !== undefined) {
        setEventDefault(PLAYER_INFO_TOGGLED_GA4_EVENT.mergeDeep({
          changed_to: videoPlayerInfoDisabled ? 'on' : 'off',
          ui_location: 'Video Player',
        }))
      }
    }

    player.on('play', function onPlay () {
      setMetaVisible(true)
      setTimeoutPlayerEvent(function onTimeout () {
        this.removeClass('vjs-play-event')
      }, 1000)
      this.addClass('vjs-play-event')
      if (isFunction(onVideoPlay)) {
        onVideoPlay()
      }
    })
    player.on('pause', function onPause () {
      setTimeoutPlayerEvent(function onTimeout () {
        this.removeClass('vjs-pause-event')
      }, 1000)
      this.addClass('vjs-pause-event')
    })
    player.on('canplay', async function onLoadedMetaData () {
      try {
        await this.play()
      } catch (e) {
        // Do nothing the brower might not allow playback
      }
      setLoading(false)
      self.setState({ sendGa4Events: true })
    })
    // Handle reload of expired media info
    player.on('timeupdate', () => {
      if (!media.getIn(['data', 'expiresTime'])) {
        return
      }
      // Check if the current media has expired.
      const date = new Date()
      const currentTime = date.getTime()
      // Once it has expired just reload the page.
      if (currentTime > media.getIn(['data', 'expiresTime'])) {
        onceReloadLocation()
      }
    })
    player.on('ended', (e) => {
      setVideoPlayerEnded(true)
      if (isFunction(onVideoEnded)) {
        onVideoEnded(e)
      }
    })
    player.on('ended', function onEnded () {
      try {
        if (this.isFullscreen()) {
          this.exitFullscreen()
        }
      } catch (e) {
        // Do thing player failed to check full screen or exit
      }
    })
    player.on('fullscreenchange', () => {
      const isFullscreen = document.fullscreenElement
        || document.mozFullScreenElement
        || document.webkitFullscreenElement
      setVideoPlayerFullscreen(isFullscreen)
    })
    player.on('useractive', () => {
      setMetaAutoHideVisibility(true, 'videoPlayer')
    })
    player.on('userinactive', () => {
      setMetaAutoHideVisibility(false)
    })
    //
    player.on('ratechange', function onRateChange () {
      const playbackRate = this.playbackRate()
      setLocalPreferences(auth.get('uid'), LOCAL_PREFERENCE_VIDEO_PLAYBACK_RATE, playbackRate, auth)
    })

    const logVideoPlaybackErrorToEventLog = () => {
      setEventDefault(APP_ERROR_EXPERIENCED_GA4_EVENT.mergeDeep({
        ...self.getGa4EventData(),
        error_type: 'VideoPlaybackError',
      }))
    }
    const logStalledPlaybackError = throttle(() => {
      const { sendGa4Events, videoReady } = self.state
      if (!sendGa4Events || !videoReady) return
      logVideoPlaybackErrorToEventLog()
    }, 45000, { leading: true, trailing: false })

    player.on('error', () => {
      logVideoPlaybackErrorToEventLog()
    })
    player.on('stalled', logStalledPlaybackError)

    // Custom components are available up to this point
    player.one('canplay', () => {
      const { videoOverlayButton } = player.controlBar
      if (videoOverlayButton) videoOverlayButton.on('click', videoOverlayButtonClicked)
    })
  }

  configureDefaultPlugins = (player) => {
    const { props } = this
    const {
      videoPlayerInfoDisabled,
      setFeatureTrackingDataPersistent,
      media,
      video,
      staticText,
      auth,
      userLanguages,
      setEventVideoAudioChanged,
      aiVideoGuideHidden,
    } = props
    const forward = 30
    const back = 10
    // eslint-disable-next-line no-template-curly-in-string
    const seekBack = replace(staticText.get('seekBack'), '${ seekBack }', back)
    // eslint-disable-next-line no-template-curly-in-string
    const seekForward = replace(staticText.get('seekForward'), '${ seekForward }', forward)
    const toggleVideoInfoButtonHide = staticText.get('toggleVideoInfoButtonHide')
    const toggleVideoInfoButtonShow = staticText.get('toggleVideoInfoButtonShow')

    player.KeyboardPlaybackToggle({ keys: [KEY_SPACEBAR] })
    player.SeekButtons({
      forward, back, seekBack, seekForward,
    })
    player.VideoPlayerApps({
      render: (_player) => renderVideoPlayerApps(props, _player),
    })
    player.VideoOverlayButtons({
      videoPlayerInfoDisabled,
      setFeatureTrackingDataPersistent,
      toggleVideoInfoButtonHide,
      toggleVideoInfoButtonShow,
    })

    // VIDEO MEDIA CENTER PLUGIN
    const mediaData = media.get('data', Map())
    const activeAudioLanguageTrack = getActiveAudioLanguageTrack(video, auth, userLanguages, media)
    const activeSubtitlesLanguageTrack = getSubtitlesPreferences(video, auth)
    const appLanguage = userLanguages.get(0, null)
    const textTracks = mediaData.get('textTracks', Map())
    const audioTracks = mediaData.get('byLang')

    // initialization
    player
      .VideoMediaCenter({
        staticText,
        userLanguages,
        activeAudioLanguageTrack,
        activeSubtitlesLanguageTrack,
        appLanguage,
        textTracks,
        audioTracks,
      })

    // side-effects
    player
      .VideoMediaCenter()
      .on('audio-track-changed', (event, details = {}) => {
        const { lang } = details
        if (!isNil(lang)) {
          saveAudioPreferences(lang, video, auth)
          setEventVideoAudioChanged({ lang })
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            'audio-track-changed is triggered, but the event does not contain information about the language of the track',
          )
        }
      })

    player
      .VideoMediaCenter()
      .on('text-state-changed', (event, details = {}) => {
        const { lang } = details
        if (!isNil(lang)) {
          saveSubtitlesPreferences(lang, video, auth)
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            'text-state-changed is triggered, but the event does not contain information about the language of the track',
          )
        }
      })
    // END OF VIDEO MEDIA CENTER PLUGIN

    // VIDEO CHAPTERS PLUGIN
    const chaptersData = mediaData.get('textTracks', Map()).get('chapters', Map())

    // Get chapters static text
    const chaptersStaticText = Map({
      chaptersLanguage: 'Chapters Language',
      noChapters: 'No chapters available',
      chapters: 'Chapters',
    })

    // initialization
    player
      .VideoChapters({
        staticText: chaptersStaticText,
        userLanguages,
        chapters: chaptersData,
        appLanguage,
        auth,
      })
    // END OF VIDEO CHAPTERS PLUGIN

    player.VolumeBar({
      staticText,
    })
    // Use the PlayFromBeginningButton plugin
    player.PlayFromBeginningButton()

    // ORACLE GUIDE ON SIDE PLUGIN
    const gotsEnabled = get(config, ['features', 'ai', 'oracleGuideOnTheSide'], false)
    const isLoggedIn = getAuthIsLoggedIn(auth)

    if (gotsEnabled && isLoggedIn && !aiVideoGuideHidden) {
      const { store, apolloClient, history } = props
      const rawContentId = video.getIn(['data', 'id'])
      const rawFeatureMediaId = video.getIn(['data', 'feature', 'id'])
      const contentId = rawContentId != null ? String(rawContentId) : null
      const featureMediaId = rawFeatureMediaId != null ? String(rawFeatureMediaId) : null
      const jwt = getAuthJwt(auth) || ''

      // Callback for control bar button to open the guide panel
      const onOpenGuide = () => {
        const actions = getBoundActions(store.dispatch)
        actions.oracle.setOracleGuideOnSideClosed(false)
      }

      player.OracleGuideOnSide({
        contentId,
        featureMediaId,
        jwt,
        store,
        apolloClient,
        history,
        render: renderOracleGuideOnSide,
        onOpenGuide,
      })
    }
    // END OF ORACLE GUIDE ON SIDE PLUGIN
  }

  configureSmfPlugin = (player) => {
    const {
      props,
    } = this
    const {
      auth,
      user,
      type,
      media,
      video,
      upstreamContext = Map(),
      setEventDataVideoViewQualified,
      setEventDefault,
      setMetaVotesVisible,
      sendOptimizelyFeatureEvent,
    } = props
    const mediaId = media.get('id')
    const uid = auth.get('uid', null)
    const jwt = auth.get('jwt', null)
    const videoId = video.getIn(['data', 'id'])
    const mediaLang = media.getIn(['data', 'mediaLang'])
    const duration = this.getDuration()
    const anonymousUuid = user.getIn(['data', 'anonymousUuid'])
    const recommId = upstreamContext.getIn(['playEventData', 'recommId']) || null

    player.SmfAnalytics({
      uid,
      jwt,
      mediaId,
      videoId,
      mediaLang,
      duration,
      anonymousUuid,
      recommId,
      onPlayEvent: ((videoAnalyticsId) => {
        // GA4 event
        setEventDefault(VIDEO_LOADS_AND_STARTS_PLAY_GA4_EVENT.mergeDeep({
          ...this.getGa4EventData(),
          ...this.getGa4TileData(),
          video_play_event_id: videoAnalyticsId,
        }))
      }),
      onQualifiedViewEvent: ((videoAnalyticsId) => {
        // Emarsys event report
        setEventDataVideoViewQualified()
        // GA4 event
        setEventDefault(VIDEO_QUALIFIED_VIEW_GA4_EVENT.mergeDeep({
          ...this.getGa4EventData(),
          ...this.getGa4TileData(),
          video_play_event_id: videoAnalyticsId,
        }))
      }),
      onWatchCompletedEvent: ((videoAnalyticsId) => {
        // GA4 event
        setEventDefault(VIDEO_COMPLETED_GA4_EVENT.mergeDeep({
          ...this.getGa4EventData(),
          ...this.getGa4TileData(),
          video_play_event_id: videoAnalyticsId,
        }))
      }),
      onVideoEngagedEvent: ((videoAnalyticsId) => {
        // GA4 event
        setEventDefault(VIDEO_ENGAGED_VIEW_GA4_EVENT.mergeDeep({
          ...this.getGa4EventData(),
          ...this.getGa4TileData(),
          video_play_event_id: videoAnalyticsId,
        }))
        if (type === TYPE_FEATURE) {
          setMetaVotesVisible(true)
        }
      }),
      onVideoInterestedEvent: ((videoAnalyticsId) => {
        // GA4 event
        setEventDefault(VIDEO_INTERESTED_VIEW_GA4_EVENT.mergeDeep({
          ...this.getGa4EventData(),
          ...this.getGa4TileData(),
          video_play_event_id: videoAnalyticsId,
        }))
        // Optimizely event for member_home_metadata_tiles metadata test
        // remove this once the metadata test is complete
        if (type === TYPE_FEATURE) {
          sendOptimizelyFeatureEvent({
            eventName: 'interested_view',
          })
        }
      }),
      onVideoFiveMinViewEvent: ((videoAnalyticsId) => {
        // GA4 event
        setEventDefault(VIDEO_FIVE_MIN_VIEW_GA4_EVENT.mergeDeep({
          ...this.getGa4EventData(),
          ...this.getGa4TileData(),
          video_play_event_id: videoAnalyticsId,
        }))
        if (type === TYPE_FEATURE) {
          sendOptimizelyFeatureEvent({
            eventName: 'video_5_min_view',
            video_play_event_id: videoAnalyticsId,
          })
        }
      }),
      onVideoAudioChangedEvent: ((videoAnalyticsId, value) => {
        // GA4 event
        setEventDefault(VIDEO_AUDIO_CHANGED_GA4_EVENT.mergeDeep({
          ...this.getGa4EventData(),
          video_play_event_id: videoAnalyticsId,
          changed_to: value,
        }))
      }),
      onVideoSubtitlesSetEvent: ((videoAnalyticsId, value) => {
        // GA4 event
        setEventDefault(VIDEO_SUBTITLES_SET_GA4_EVENT.mergeDeep({
          ...this.getGa4EventData(),
          video_play_event_id: videoAnalyticsId,
          changed_to: value,
        }))
      }),
      onChromecastConnected: ((videoAnalyticsId) => {
        // GA4 event
        setEventDefault(CHROMECAST_CONNECTED_GA4_EVENT.mergeDeep({
          ...this.getGa4EventData(),
          ...this.getGa4TileData(),
          video_play_event_id: videoAnalyticsId,
        }))
      }),
      onAirplayConnected: ((videoAnalyticsId) => {
        // GA4 event
        setEventDefault(AIRPLAY_CONNECTED_GA4_EVENT.mergeDeep({
          ...this.getGa4EventData(),
          ...this.getGa4TileData(),
          video_play_event_id: videoAnalyticsId,
        }))
      }),
      onSpeedChanged: ((videoAnalyticsId, playbackRate) => {
        const { videoReady, sendGa4Events } = this.state
        if (sendGa4Events && videoReady) {
          // GA4 event
          setEventDefault(VIDEO_SPEED_CHANGED_GA4_EVENT.mergeDeep({
            ...this.getGa4EventData(),
            changed_to: playbackRate,
            video_play_event_id: videoAnalyticsId,
          }))
        }
      }),
    })
  }

  configureNewRelicPlugin = async (player) => {
    const nrConfig = get(config, 'newRelicVideo', {})
    const { applicationID, licenseKey } = nrConfig
    if (!applicationID || !licenseKey) {
      return
    }
    const { props } = this
    const {
      app,
      video,
      media,
      auth,
      analyticsMeta = Map(),
    } = props
    const uid = auth.get('uid', null)
    const viewerId = get(analyticsMeta, 'viewerId', null) || uid
    const title = Map.isMap(video) ? video.getIn(['data', 'title']) : null
    const contentId = Map.isMap(video) ? video.getIn(['data', 'id']) : null
    const mediaId = Map.isMap(media) ? media.getIn(['data', 'id']) : null
    const durationSec = this.getDuration()
    const duration = typeof durationSec === 'number' && durationSec > 0 ? durationSec : null
    if (player && (title || contentId || mediaId || duration != null)) {
      const p = player
      p.mediainfo = p.mediainfo || {}
      if (title) p.mediainfo.name = title
      if (contentId != null) p.mediainfo.id = String(contentId)
      else if (mediaId != null) p.mediainfo.id = String(mediaId)
      if (duration != null) p.mediainfo.duration = duration
    }
    const beacon = nrConfig.beacon || 'bam.nr-data.net'
    try {
      const mod = await import('@newrelic/video-videojs')
      // Package may export the class as default (CJS) or as default.default (ESM namespace)
      const VideojsTracker = mod.default?.default ?? mod.default
      if (typeof VideojsTracker !== 'function') {
        return
      }
      const info = {
        applicationID,
        licenseKey,
        beacon,
      }
      const customData = {
        isLive: false,
        streamType: 'vod',
        appName: Map.isMap(app) ? app.get('name', 'web-app') : 'web-app',
        appVersion: Map.isMap(app) ? app.get('version', '0.0.0') : '0.0.0',
        playerComponent: 'VideoPlayer',
        authState: Map.isMap(auth) && auth.get('jwt') ? 'logged-in' : 'anonymous',
      }
      if (nrConfig.parentAppId) customData.parentAppId = nrConfig.parentAppId
      if (nrConfig.mediaAccountId) customData.mediaAccountId = nrConfig.mediaAccountId
      const brightcovePlayerId = get(config, ['brightcove', 'player'], null)
      if (brightcovePlayerId != null && brightcovePlayerId !== '') {
        customData.brightcovePlayerId = String(brightcovePlayerId)
      }
      const genre = Map.isMap(video) ? video.getIn(['data', 'admin_category', 'name'], '') : ''
      if (genre) customData.genre = String(genre)
      const previewMediaId = Map.isMap(video) ? video.getIn(['data', 'preview', 'id']) : null
      const featureMediaId = Map.isMap(video) ? video.getIn(['data', 'feature', 'id']) : null
      let mediaContentReferenceType = 'unknown'
      if (mediaId && previewMediaId && String(mediaId) === String(previewMediaId)) {
        mediaContentReferenceType = 'preview'
      } else if (mediaId && featureMediaId && String(mediaId) === String(featureMediaId)) {
        mediaContentReferenceType = 'feature'
      }
      customData.mediaContentReferenceType = mediaContentReferenceType
      const tracker = new VideojsTracker(player, { info, customData })
      if (viewerId != null && viewerId !== '') {
        tracker.setUserId(String(viewerId))
      }
      this._newRelicTracker = tracker
    } catch (err) {
      // Tracker failed to load or initialize
    }
  }

  configurePlayerPlugins = (player) => {
    this.configureDefaultPlugins(player)
    this.configureSmfPlugin(player)
    this.configureNewRelicPlugin(player)
  }

  canRenderVideo = () => {
    const { props } = this
    const {
      type,
      auth,
      video,
      userVideo,
      media,
      location,
      checkPath,
      playerSrc,
    } = props

    // if playerSrc is provided, we don't need to wait for other data
    if (playerSrc) {
      return true
    }

    const processing = video.get('processing')
      || userVideo.get('processing')
      || media.get('processing')
    const pathReady = checkPath
      ? video.getIn(['data', 'path']) === location.pathname.replace(/^\//, '')
      : true
    const mediaReady = video.getIn(['data', type, 'id']) === media.getIn(['data', 'id'])
    const userVideoReady = !auth.get('jwt')
      || location.query.fullplayer === TYPE_PREVIEW
      || video.getIn(['data', 'id']) === userVideo.getIn(['data', 'id'])
    if (processing || !pathReady || !mediaReady || !userVideoReady) {
      return false
    }
    return true
  }

  playOrPause = async (shouldPlay) => {
    const { _player } = this
    if (!_player) {
      return
    }
    try {
      if (shouldPlay) {
        await _player.play()
      } else {
        await _player.pause()
      }
    } catch (e) {
      // Do nothing player call failed
    }
  }

  throttledSeek = throttle((direction, amount) => {
    if (!this._player) return
    const SEEK_AMOUNT = amount // seconds
    const current = this._player.currentTime()
    if (direction === 'backward') {
      this._player.currentTime(Math.max(0, current - SEEK_AMOUNT))
    } else if (direction === 'forward') {
      this._player.currentTime(Math.min(this._player.duration(), current + SEEK_AMOUNT))
    }
  }, SEEK_THROTTLE_INTERVAL_MS, { trailing: false })

  handleGlobalKeyDown = (e) => {
    // Ignore if focus is on an input, textarea, or select
    if ([
      'INPUT',
      'TEXTAREA',
      'SELECT',
    ].includes(e.target.tagName)) return
    if (!this._player) return
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      this.throttledSeek('backward', 5)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      this.throttledSeek('forward', 5)
    } else if (e.key === 'j' || e.key === 'J') {
      e.preventDefault()
      this.throttledSeek('backward', 10)
    } else if (e.key === 'i' || e.key === 'I') {
      e.preventDefault()
      this.throttledSeek('forward', 10)
    } else if (e.key === 'Home' || e.key === '0') {
      e.preventDefault()
      this._player.currentTime(0)
    }
  }

  render () {
    const {
      props, state, _player, playOrPause,
    } = this
    const {
      user,
      video,
      videoPlayer,
      posterOverride,
      playsinline = false,
      videoReady,
      touchable,
      auth,
      type,
    } = props
    const { containerStyles, renderPlayer } = state
    const { account, player } = get(config, 'brightcove', {})
    const poster = posterOverride || video.getIn(['data', 'poster'], '')
    const vjsOpts = getVideoOpts({
      user,
      poster,
      controls: true,
      loop: false,
      isLive: false,
      playsinline: playsinline || isIOS(),
      inactivityTimeout: touchable
        ? MOBILE_VIDEO_PLAYER_INACTIVITY_TIMEOUT
        : VIDEO_PLAYER_INACTIVITY_TIMEOUT,
    })
    const authJwt = auth.get('jwt')
    const userLanguage = getPrimary(user.getIn(['data', 'language'], List()))

    if (dataHasError(props)) {
      return (
        <div className="video-player">
          <p className="video-player__error">
            The video player experienced an error loading this video, please
            refresh the page to try again or select another video.
          </p>
        </div>
      )
    }

    if (!videoReady && !this.canRenderVideo()) {
      return <VideoPlayerSpinner video={video} />
    }

    return (
      <div
        className="video-player"
        tabIndex={0}
        onFocus={this.onPlayerFocus}
        onBlur={this.onPlayerBlur}
        onMouseOver={this.onPlayerMouseOver}
        onMouseOut={this.onPlayerMouseOut}
      >
        {videoPlayer.get('loading', true) && !_player ? (
          <VideoPlayerSpinner video={video} />
        ) : null}
        {renderVideoPlayerEnded(props)}
        <div
          className="video-player__container"
          style={containerStyles}
        >
          {
            renderPlayer && <ReactPlayerLoader
              accountId={account}
              playerId={player}
              onSuccess={(data) => this.onPlayerSuccess(data)}
              onFailure={this.onPlayerFailure}
              options={vjsOpts}
            />
          }
        </div>
        {renderComments(props, _player, playOrPause)}
        {renderVideoEmailCapture(props, containerStyles)}
        {
          tilesV2GraphqlActive && authJwt && type !== TYPE_PREVIEW
            ? <ContentUpdater
                contentId={video.getIn(['data', 'id'])}
                contentType={TYPE_CONTENT_VIDEO}
                secondaryContentId={video.getIn(['data', 'seriesId'])}
                secondaryContentType={TYPE_CONTENT_SERIES}
                authJwt={authJwt}
                userLanguage={userLanguage}
              />
            : null
        }
      </div>
    )
  }
}

VideoPlayer.propTypes = {
  store: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  app: ImmutablePropTypes.map.isRequired,
  page: ImmutablePropTypes.map.isRequired,
  comments: ImmutablePropTypes.map.isRequired,
  video: ImmutablePropTypes.map.isRequired,
  media: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  tiles: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userVideo: ImmutablePropTypes.map.isRequired,
  videoPlayer: ImmutablePropTypes.map.isRequired,
  emailSignup: ImmutablePropTypes.map.isRequired,
  setMetaVisible: PropTypes.func.isRequired,
  setMetaMoreVisible: PropTypes.func.isRequired,
  setMetaVotesVisible: PropTypes.func.isRequired,
  setMetaAutoHideVisibility: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  type: PropTypes.string,
  setUserVideoDataFeaturePosition: PropTypes.func.isRequired,
  setVideoPlayerEnded: PropTypes.func.isRequired,
  deleteTiles: PropTypes.func.isRequired,
  setVideoPlayerFullscreen: PropTypes.func.isRequired,
  onVideoEnded: PropTypes.func,
  onVideoPlay: PropTypes.func,
  onPlayerReady: PropTypes.func,
  checkPath: PropTypes.bool,
  displayVideoEnded: PropTypes.bool,
  posterOverride: PropTypes.string,
  getTilesData: PropTypes.func.isRequired,
  setCommentsVisible: PropTypes.func.isRequired,
  playerSrc: PropTypes.string,
  startAt: PropTypes.number,
  analyticsMeta: PropTypes.shape({
    contentId: PropTypes.string.isRequired,
    viewerId: PropTypes.string.isRequired,
    loggedIn: PropTypes.bool.isRequired,
  }),
  staticText: ImmutablePropTypes.map.isRequired,
  optimizely: ImmutablePropTypes.map.isRequired,
  /** The time in milliseconds to delay the player render */
  playerRenderDelayMilliseconds: PropTypes.number,
  sendOptimizelyFeatureEvent: PropTypes.func.isRequired,
  primaryCourse: ImmutablePropTypes.map,
}

VideoPlayer.defaultProps = {
  checkPath: true,
  displayVideoEnded: true,
  playerRenderDelayMilliseconds: 250,
}

export default compose(
  connectRedux(
    (state) => {
      return {
        app: state.app,
        page: state.page,
        comments: state.comments,
        video: state.video,
        media: state.media,
        auth: state.auth,
        tiles: state.tiles.filter((val, key) => key === STORE_KEY_VIDEO_PLAYER_NEXT),
        user: state.user,
        userVideo: state.userVideo,
        videoPlayer: state.videoPlayer,
        upstreamContext: state.upstreamContext.get('data', Map()),
        emailSignup: state.emailSignup,
        resolver: state.resolver,
        videoPlayerInfoDisabled: state.featureTracking.getIn(
          ['data', 'disableVideoInfo'],
          false,
        ),
        staticText: state.staticText.getIn(['data', 'videoPlayer', 'data', 'default']),
        userLanguages: state.featureTracking.getIn(['data', 'userLanguages'], false),
        optimizely: state.optimizely,
        touchable: state.app.getIn(['viewport', 'touchable'], false),
        mhAiHidden: state.featureTracking.getIn(['data', 'mhAiHidden'], false),
        aiVideoGuideHidden: state.featureTracking.getIn(['data', 'aiVideoGuideHidden'], false),
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventVideoAudioChanged: actions.eventTracking.setEventVideoAudioChanged,
        setEventDataVideoEngaged:
          actions.eventTracking.setEventDataVideoEngaged,
        getTilesData: actions.tiles.getTilesData,
        setCommentsVisible: actions.comments.setCommentsVisible,
        setVideoPlayerFullscreen: actions.videoPlayer.setVideoPlayerFullscreen,
        setVideoPlayerEnded: actions.videoPlayer.setVideoPlayerEnded,
        setMetaVisible: actions.videoPlayer.setVideoPlayerMetaVisible,
        setMetaAutoHideVisibility:
          actions.videoPlayer.setVideoPlayerMetaAutoHideVisibility,
        setLoading: actions.videoPlayer.setVideoPlayerLoading,
        setMetaMoreVisible: actions.videoPlayer.setVideoPlayerMetaMoreVisible,
        setMetaVotesVisible: actions.videoPlayer.setVideoPlayerMetaVotesVisible,
        setUserVideoDataFeaturePosition:
          actions.userVideo.setUserVideoDataFeaturePosition,
        deleteTiles: actions.tiles.deleteTiles,
        setFeatureTrackingDataPersistent:
          actions.featureTracking.setFeatureTrackingDataPersistent,
        setEventDefault: actions.eventTracking.setEventDefault,
        sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
        setEventDataVideoViewQualified:
          actions.eventTracking.setEventDataVideoViewQualified,
        setAiGa4Event: actions.eventTracking.setAiGa4Event,
      }
    },
  ),
)(VideoPlayer)
